import { useHits, useInstantSearch, useSearchBox } from "react-instantsearch";
import { IndexedProduct, ProductHit } from "../../search-results/product";
import { ProductCardSkeleton } from "../../search-results/components/ProductCardSkeleton";
import { ProductHitCard } from "../../search-results/components/ProductHitCard";
import NoResults from "../../search-results/components/NoResults";

import { useState, useEffect } from "react";

/**
 * ProductHits Component
 *
 * This component displays search results using Algolia's InstantSearch hooks.
 * It shows:
 * - A loading state with skeleton placeholders while fetching results.
 * - A list of products when results are available.
 * - A "Show all results" button that redirects to the search page.
 * - A "No results" message only after the first fetch is complete.
 *
 * Fix for the NoResults flash issue:
 * - Introduces `hasFetched` state to track whether the search request has completed at least once.
 * - Prevents `<NoResults />` from displaying too soon before results are fetched.
 */
export default function ProductHits() {
  const { status } = useInstantSearch(); // Tracks search request status
  const { query } = useSearchBox(); // Retrieves the current search query
  const { items, sendEvent } = useHits<IndexedProduct>(); // Fetches search results

  const [hasFetched, setHasFetched] = useState(false);

  const handleProductClick = (hit: ProductHit) => {
    sendEvent('click', hit, 'Hit Clicked');
  };

  /**
   * Effect to track when search results have been fetched at least once.
   * Prevents `<NoResults />` from rendering prematurely before search completes.
   */
  useEffect(() => {
    if (status !== "loading" && status !== "stalled") {
      setHasFetched(true);
    }
  }, [status]);

  /**
   * Prevents flickering by not showing `<NoResults />` on first load before fetching completes.
   */
  if (!hasFetched && status === "idle") {
    return null; // Avoid showing anything until the first search completes
  }

  /**
   * Displays `<NoResults />` if there are no results after the first fetch.
   */
  if (hasFetched && status === "idle" && !items.length) {
    return <NoResults />;
  }

  return (
    <>
      <div className="md:tw-flex md:tw-flex-wrap md:tw-gap-3 md:[&>div]:tw-w-[calc(50%-0.75rem)]">
        {/* Show loading placeholders while search is in progress */}
        {((items.length === 0 && status === "loading") || status === "stalled") &&
          Array(8)
            .fill(null)
            .map((_, index) => (
              <div key={index} className="">
                <ProductCardSkeleton />
              </div>
            ))}

        {/* Display product search results */}
        {items.length > 0 &&
          items.map((item) => (
            <div key={item.objectID} className="">
              <ProductHitCard 
                productHit={item}
                onProductClick={() => handleProductClick(item)}
              />
            </div>
          ))}
      </div>

    </>
  );
}
